<template>
  <trial-to-paid :is-show-hd='isShowHDButton' v-if='$context.is.trial2paid' :is-show-subtitle="isShowSubtitle" />
  <success v-else :show-promotes='showPromotes'>
      <thanks v-if='isShowHDButton && !hasOneTimeWebinar' v-bind="thanksProps">
        <get-started />
        <download-action v-if='isShowDownload' />
      </thanks>
      <thanks v-else-if='hasOneTimeWebinar' v-bind="thanksProps">
        <view-account />
        <download-action v-if='isShowDownload' />
      </thanks>
      <thanks v-else-if='isShowSetupCta' v-bind="thanksProps">
        <template #subtitle>
          <span v-if="isDownsell" v-html="$t('billing.success.downgrade_txt')" />
        </template>
        <subtitle :sub-title="$t('billing.success.confirmation.set_up_desc')" />
        <set-up-action :eligibleProductTypeJson='eligibleProductTypeJson' />
        <download-action v-if='isShowDownload' />
      </thanks>
      <!-- upsell -->
      <thanks v-else-if='isUpsell' v-bind="thanksProps">
        <subtitle :sub-title="$t('billing.success.confirmation.user_assign_desc')" />
        <user-management-cta :cta-name="$t('billing.success.confirmation.user_cta')" />
        <download-action v-if='isShowDownload' />
      </thanks>
      <thanks v-else-if='isNeedAssignsUser' v-bind="thanksProps">
        <template #subtitle>
          <span  v-html="$t('billing.success.downgrade_txt')" />
        </template>
        <subtitle :sub-title='assignText' />
        <user-management-cta :cta-name="$t('billing.success.unassign_users')" />
        <view-account type='plain' />
        <download-action v-if='isShowDownload' />
      </thanks>
      <thanks v-else>
        <template #subtitle>
          <span v-if='isDownsell' v-html="$t('billing.success.downgrade_txt')" />
        </template>
        <view-account />
        <download-action v-if='isShowDownload' />
      </thanks>
  </success>
</template>

<script>
import Success from '@/views/succezz/facade.vue';
import Thanks from '@/views/succezz/thanks-template';
import DownloadAction from '@/views/succezz/thanks-template/download-action.vue';
import SetUpAction from '@/views/succezz/thanks-template/set-up-action.vue';
import { mapGetters } from 'vuex';
import { BASE_PLAN_SUB_TYPES, BILLING_CYCLE, companySetupProductTypeMap, planTypes } from '@/common/constants';
import ViewAccount from '@/views/succezz/thanks-template/view-account.vue';
import { isEmptyVal, safeJSONParse } from '@/utils';
import UserManagementCta from '@/views/succezz/thanks-template/user-management-cta.vue';
import { dateTimeFormat } from '@/utils/dateformat';
import Subtitle from '@/views/succezz/thanks-template/subtitle.vue';
import { includes } from 'lodash';
import common from '@/views/succezz/mixin/common';
import TrialToPaid from '@/views/succezz/components/trial-to-paid.vue';
import GetStarted from '@/views/succezz/thanks-template/get-started.vue';
import { flowTypes } from '@/common/service/page-config';
import { getApmFlowType } from '@/views/succezz/utils';
import { isIAPRedirectUser } from '@/utils/iap'

export default {
  name: "Meeting",
  mixins: [common],
  components: {
    GetStarted,
    TrialToPaid,
    Subtitle,
    UserManagementCta,
    ViewAccount,
    SetUpAction,
    DownloadAction,
    Success,
    Thanks,
  },
  computed: {
    ...mapGetters(['boughtPlans', 'getCurrentBasePlanInfo', 'successConfig', 'persistState']),
    thanksProps() {
      return {
          email: this.email,
          title: this.title,
      }
    },
    eligibleProductTypeJson() {
      return this.successConfig?.eligibleProductTypeJson || '[]';
    },
    companySetupProductTypeList() {
      const eligibleProductType = safeJSONParse(this.eligibleProductTypeJson, []);
      return isEmptyVal(eligibleProductType) ?
        safeJSONParse(this.$config?.('eligibleProductTypeJson') || '[]', [])
        : eligibleProductType;
    },
    isJustSetupOneProduct() {
      return (this.companySetupProductTypeList || []).length === 1;
    },
    companySetupZp() {
      return this.isJustSetupOneProduct && includes(this.companySetupProductTypeList, companySetupProductTypeMap.pbx);
    },
    isShowSetupCta() {
      const isEligible = !!safeJSONParse(this.eligibleProductTypeJson).length && !this.companySetupZp && !this.isNeedAssignsUser;
      return (this.$context.is.buyNow || this.isBasic2Meeting) ? this.isAllowSetup && isEligible : isEligible;
    },
    isShowDownload() {
      // only for NN
      if (this.$context.is.modal) {
        return false;
      } else if (isIAPRedirectUser) {
        return false;
      } else if (this.$context.is.buyNow || this.isProFreeTrial || getApmFlowType() === flowTypes.buyNow) return true;
      return false;
    },
    isBasic2Meeting() {
      return this.$context.is.upgrade && this.$context.type === planTypes.ZOOM_MEETING;
    },
    meetingPlan() {
      return this.boughtPlans.find(p => p.type === planTypes.ZOOM_MEETING) ?? {cartInfo: {}};
    },
    isAPM() {
      return !!this.persistState.confirmationDetails?.isAPM;
    },
    isOnlyBuyOneProLicense() {
      const proPlan = this.getSelectedPlans.find(p => p.type === planTypes.ZOOM_MEETING && p.category === BASE_PLAN_SUB_TYPES.PRO);
      return proPlan?.cartInfo?.selected_number === 1;
    },
    isShowHDButton() {
      const is = this.$context.is;
      if (this.isEligible4HomeDestination) {
        if (this.isAPM) {
          return this.isOnlyBuyOneProLicense;
        }
        if (is.buyNow || is.basic2pro || is.add2cart || is.buyTrial || is.trial2paid || is.mixBuy) {
          return this.isOnlyBuyOneProLicense;
        }
      }
      return false;
    },
    isAllowSetup() {
      // for multiple users
      if (this.meetingPlan?.category === BASE_PLAN_SUB_TYPES.PRO && this.meetingPlan?.cartInfo?.selected_number > 1) {
        return true;
      }
      // HD page don't support biz plan yet!
      if (this.meetingPlan?.category === BASE_PLAN_SUB_TYPES.BIZ) {
        return true;
      }
      return false;
    },
    isNeedAssignsUser() {
      if (this.unassignLicenses) {
        return true;
      }
      return false;
    },
    unassignLicenses() {
      const quantity = this.persistState.downgradeState?.unAssignLicenseQuantity;
      return quantity > 0 ? quantity : false;
    },
    billingDate() {
      return dateTimeFormat(this.persistState.downgradeState?.planExpireTime);
    },
    assignText() {
      return this.$t('billing.success.downgrade_unassign_licenses', [this.unassignLicenses, this.billingDate]);
    },
    isDownsell() {
      if (!this.$context.is.edit) return false;
      if (this.isNothingChanged) return false;
      if (this.isNeedAssignsUser) return true;
      const { has_downgrade_request, has_decrease_request, has_same_pending_plan } = this.meetingPlan.cartInfo;
      return (has_downgrade_request || has_decrease_request || has_same_pending_plan);
    },
    isUpsell() {
      if (!this.$context.is.edit) return false;
      if (this.isOnlyChangeCycle) return false;
      if (this.isNothingChanged) return false;
      return !this.isDownsell;
    },
    showPromotes() {
      if (this.isDownsell || this.isShowSetupCta || this.isProFreeTrial) return false;
      if (this.$context.is.buyNow || this.$context.is.basic2pro || this.$context.is.trial2paid) {
        if (this.$config('isEduCoppa')) return false;
        if (this.successConfig.needBlock4currencySwitch) return false;
        if (this.isAPM) return false;
        return this.isOnlyBuyOneProLicense;
      }
      return false;
    },
    getSelectedPlans() {
      return this.persistState.confirmationDetails.getSelectedPlans;
    },
    hasOneTimeWebinar() {
      return this.getSelectedPlans.find(p => p.type === planTypes.WEBINAR && p.billing_cycle === BILLING_CYCLE.ONE_TIME);
    },
    originalBoughtPlans() {
      return this.persistState.confirmationDetails.boughtPlans;
    },
    isOnlyChangeCycle() {
      const meetingPlan = this.originalBoughtPlans?.find?.(p => p.type === planTypes.ZOOM_MEETING);
      if (!meetingPlan) return false;
      if (this.meetingPlan.cartInfo.selected_number === meetingPlan.quantity
        && this.meetingPlan.billing_cycle !== meetingPlan.billingCycle) {
        return true;
      }
      return false;
    },
    isNothingChanged() {
      const meetingPlan = this.originalBoughtPlans?.find?.(p => p.type === planTypes.ZOOM_MEETING);
      if (!meetingPlan) return false;
      if (this.meetingPlan.cartInfo.selected_number === meetingPlan.quantity && this.meetingPlan.billing_cycle === meetingPlan.billingCycle) {
        return true;
      }
      return false;
    },
    isProFreeTrial() {
      return this.$context.is.buyTrial && this.meetingPlan?.isOnlineFreeTrial;
    },
    isEligible4HomeDestination() {
      return this.$config('isEligible4HomeDestination');
    },
    isShowSubtitle() {
      return true;
    }
  },
};
</script>
